import { Device, MarketLocation, usePowerLevels } from '@e2m-customer-portal/hooks';
import { DeviceName, ErrorComponent, LoadingError, PageTitle, SingleSelect, Toolbar } from '@e2m-customer-portal/ui';
import { isPast } from 'date-fns';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDisplayName } from '../../../utils';
import { AnnualDeviceTable } from './AnnualDeviceTable';
import { AnnualDeviceTablePlaceholder } from './AnnualDeviceTablePlaceholder';
import { useDefaultForecast } from './queries';

export interface AnnualPlanningTableProps {
  marketLocation: MarketLocation;
}

function DeviceItemComponent({ value: device }: Readonly<{ value: Device }>): ReactElement {
  return (
    <div className="flex flex-col" data-testid={device.name}>
      <span className="text-sm font-bold">{getDisplayName(device)}</span>
      {device.externalName && <span className="text-grey-300 text-sm">{device.name}</span>}
    </div>
  );
}

export function AnnualPlanningTable({
  marketLocation,
}: Readonly<AnnualPlanningTableProps>): ReactElement<AnnualPlanningTableProps> {
  const [t] = useTranslation();

  const devices = marketLocation.machines.flatMap((machine) =>
    machine.devices.map((device) => ({ ...device, installedPower: machine.installedPower })),
  );
  const [selectedDevice, setSelectedDevice] = useState(devices[0]);
  const { loading, data, error } = useDefaultForecast(selectedDevice.id, {
    skip: !selectedDevice,
  });
  const powerLevels = usePowerLevels(selectedDevice.id);

  if (error) {
    return <ErrorComponent error={error} />;
  }

  return (
    <div>
      <PageTitle label={t('MASTER_DATA_TAB_PLANT')} title={marketLocation.externalName || marketLocation.name} />
      <Toolbar
        left={
          <SingleSelect
            className="w-full max-w-[40ch]"
            label={t('SHARED_TECHNICAL_UNIT_INPUT_LABEL')}
            options={devices}
            value={selectedDevice}
            onChange={setSelectedDevice}
            SelectComponent={DeviceName}
            ItemComponent={DeviceItemComponent}
          />
        }
      />
      {loading ? (
        <AnnualDeviceTablePlaceholder />
      ) : (
        <LoadingError loading={loading} error={error}>
          {data && (
            <AnnualDeviceTable
              deviceId={selectedDevice.id}
              defaultForecast={data.getDefaultForecast}
              installedPower={selectedDevice.installedPower}
              powerLevels={powerLevels ?? []}
              editable={
                marketLocation.permissions.editForecast &&
                !(marketLocation.marketingEnd != null && isPast(marketLocation.marketingEnd))
              }
            />
          )}
        </LoadingError>
      )}
    </div>
  );
}
